import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { OrderStatus } from '../types/orders.js';

export interface StatusChangeDialogProps {
  orderNumber: string;
  validNextStatuses: OrderStatus[];
  onClose: () => void;
  onSubmit: (input: { status: OrderStatus; note?: string }) => void;
  isSubmitting: boolean;
}

/** Same accessible-dialog pattern as StockAdjustmentDialog (Admin Dashboard Milestone 2) -- focus-on-open, Escape-to-cancel, backdrop-click-to-cancel. `cancelled` is deliberately never offered here -- it has its own dedicated dialog/endpoint (CancelOrderDialog) since only that path requires a reason and releases inventory. */
export function StatusChangeDialog({
  orderNumber,
  validNextStatuses,
  onClose,
  onSubmit,
  isSubmitting,
}: StatusChangeDialogProps) {
  const selectableStatuses = validNextStatuses.filter((status) => status !== 'cancelled');
  const [status, setStatus] = useState<OrderStatus | ''>(selectableStatuses[0] ?? '');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const selectId = useId();
  const noteId = useId();

  useEffect(() => {
    selectRef.current?.focus();
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  function handleConfirm() {
    if (!status) {
      setError('Choose a status.');
      return;
    }
    setError(null);
    onSubmit({ status, note: note.trim() || undefined });
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
      >
        <h2 className="text-base font-semibold text-gray-900">Change status — {orderNumber}</h2>

        {selectableStatuses.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">
            This order has no further status transitions available.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            <div>
              <label htmlFor={selectId} className="mb-1 block text-xs font-medium text-gray-600">
                Next status
              </label>
              <select
                ref={selectRef}
                id={selectId}
                value={status}
                onChange={(event) => setStatus(event.target.value as OrderStatus)}
                className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm capitalize focus-visible:outline-none focus-visible:ring-2"
              >
                {selectableStatuses.map((option) => (
                  <option key={option} value={option}>
                    {option.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={noteId} className="mb-1 block text-xs font-medium text-gray-600">
                Note (optional)
              </label>
              <textarea
                id={noteId}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={2}
                className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
              />
            </div>
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-full border border-gray-200 px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
          {selectableStatuses.length > 0 && (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isSubmitting}
              className="bg-brand text-brand-foreground rounded-full px-4 py-1.5 text-sm font-semibold disabled:opacity-60"
            >
              {isSubmitting ? 'Applying…' : 'Apply'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
